import { Howl } from 'howler'
import { IReactionDisposer, reaction } from 'mobx'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import { GASendEvent } from '../ga'
import { PlayerState } from '../models/player'
import { IRootStore } from '../models/root'
import { ISequenceEvent, ScriptState } from '../models/script'
import Timer from '../Timer'
import { IFileNode, IModuleDataResponse } from './Module'

// tslint:disable-next-line:no-var-requires
const NoSleep = require('../NoSleep')

interface IWrappedHowl {
  loaded: boolean
  howl: Howl
}

interface IPlayerProps {
  data: IModuleDataResponse
}

interface InjectedProps extends IPlayerProps {
  store: IRootStore
}

@inject('store')
@observer
class Player extends React.Component<IPlayerProps> {
  private howls: Map<string, IWrappedHowl>
  private allFiles: IFileNode[]
  private timer: Timer
  private progressInterval: number
  private curHowl?: Howl
  private disposer: IReactionDisposer
  private nosleep = new NoSleep()

  get injected() {
    return this.props as InjectedProps
  }

  public componentDidMount() {
    const { store } = this.injected

    // Add a reaction to observe changes to player state
    this.disposer = reaction(
      () => this.injected.store.player.state,
      this.playerStateChangeHandler
    )

    this.loadPlayer()

    if (this.nosleep) {
      this.nosleep.enable()
    }

    // Log start
    if (store.script.data) {
      GASendEvent('module', 'started', store.script.data.shortname)
    }
  }

  public componentWillUnmount() {
    this.clearAllPlayData()

    if (this.nosleep) {
      this.nosleep.disable()
    }

    try {
      if (window && window.drift && typeof window.drift !== 'undefined') {
        window.drift.api.widget.show()
        return
      }
    } catch (e) {
      //
    }
  }

  public render() {
    return <div className="player" />
  }

  private loadPlayer() {
    const { store } = this.injected

    // Ensure our script is loaded
    if (store.script.state === ScriptState.loading || !store.script) {
      store.throwTopLevelError(
        `Player tried to load, but the script for this module hasn't loaded (${
          store.script.state
        }).`
      )

      return
    }

    this.loadAudioFiles()
  }

  private loadAudioFiles() {
    const { data } = this.props
    const { store } = this.injected

    // Reset howls
    this.howls = new Map<string, IWrappedHowl>()

    this.allFiles = data.allFile.edges.map(edge => edge.node)

    const scriptData = store.script.data

    if (!scriptData) {
      store.throwTopLevelError(
        `Player tried to load audio, but script data is null.`
      )

      return
    }

    // Load all required howls
    scriptData.sequence.map(sequenceItem => {
      const audioEvents = sequenceItem.events.filter(
        (event): event is ISequenceEvent => event.type === 'audio'
      )

      audioEvents.map(event => {
        this.loadWrappedHowl(event.fileName)
      })
    })
  }

  private loadWrappedHowl(fileName: string) {
    const { store } = this.injected

    // Avoid duplicates of audio files
    if (this.howls.get(fileName)) {
      return
    }

    // Find all files with our given audio filename
    const audioFiles = this.allFiles.filter(file => file.name === fileName)
    let mp3FileURL = ''
    let webmFileURL = ''

    audioFiles.map(file => {
      const ext = file.publicURL.substr(file.publicURL.lastIndexOf('.') + 1)

      if (ext === 'mp3') {
        mp3FileURL = file.publicURL
      } else if (ext === 'webm') {
        webmFileURL = file.publicURL
      }
    })

    // Ensure our audio files exist
    if (!mp3FileURL || !webmFileURL) {
      store.throwTopLevelError(
        `Player tried to load audio, but failed to find correct audio files. Expected mp3: ${mp3FileURL}; webm: ${webmFileURL}`
      )

      return
    }

    const wrappedHowl = {
      howl: new Howl({
        html5: true,
        onload: this.onHowlLoaded.bind(this, fileName),
        src: [webmFileURL, mp3FileURL],
      }),
      loaded: false,
    }

    this.howls.set(fileName, wrappedHowl)
  }

  private onHowlLoaded(fileName: string) {
    const { store } = this.injected

    const howl = this.howls.get(fileName)

    if (!howl) {
      store.throwTopLevelError(
        `Player failed to find "${fileName}" in howl map: ${this.howls}`,
        () =>
          new Promise((resolve, reject) => {
            this.loadAudioFiles() // Try to reload the audio
            resolve()
          })
      )

      return
    }

    // Mark howl as loaded
    howl.loaded = true

    // Check # of loaded cards
    let unloadedCount = 0

    this.howls.forEach(wrappedHowl => {
      if (!wrappedHowl.loaded) {
        unloadedCount++
      }
    })

    const percentageLoaded = Math.round(
      (this.howls.size - unloadedCount) / this.howls.size * 100
    )

    store.player.updateProgressPercentage(percentageLoaded)

    if (!unloadedCount) {
      this.onAllHowlsLoaded()
      return
    }
  }

  private onAllHowlsLoaded() {
    const { store } = this.injected

    if (store.player.state === PlayerState.loading) {
      store.player.updateProgressPercentage(0)
      store.player.setPlayerState(PlayerState.loaded)

      this.playCard()
    }
  }

  private playCard() {
    const { store } = this.injected

    if (!store.activeCard || !store.activeEvent) {
      store.throwTopLevelError(
        `Called playCard(), but store.activeCard or store.activeEvent is null`
      )

      return
    }

    this.playEvent(store.activeEvent)
  }

  private playEvent(event: ISequenceEvent) {
    const { store } = this.injected

    // Force stop any existing howl
    if (this.curHowl) {
      this.curHowl.stop()
      delete this.curHowl
    }

    const wrappedHowl = this.howls.get(event.fileName)

    if (!wrappedHowl) {
      store.throwTopLevelError(
        `Attempted to play event "${event}", but could not get wrappedHowl from howls.`
      )

      return
    }

    this.curHowl = wrappedHowl.howl
    this.addHowlEventListeners(this.curHowl)

    const extraTime = event.extraTime
    const duration = (this.curHowl.duration() + extraTime) * 1000 // s -> ms

    this.timer = new Timer(
      duration,
      undefined,
      undefined,
      this.timerOnEndHandler
    )

    // store.player.setPlayerState(PlayerState.playing);
    this.curHowl.play()
  }

  private addHowlEventListeners(howl: Howl) {
    howl.on('play', this.howlOnPlayHandler.bind(this, howl))
    howl.on('end', this.howlOnEndHandler.bind(this, howl))
  }

  private howlOnPlayHandler(howl: Howl) {
    const { store } = this.injected

    // Special case for initial play: timer starts when the player starts
    // to avoid lag
    if (this.timer && !this.timer.isPlaying) {
      this.timer.play()
      store.player.setPlayerState(PlayerState.playing)
    }
  }

  private howlOnEndHandler(howl: Howl) {
    delete this.curHowl
  }

  private timerOnEndHandler = () => {
    const { store } = this.injected
    store.completeEvent()
  }

  private updateProgressPercentage = () => {
    const { store } = this.injected
    store.player.updateProgressPercentage(this.timer.getPercentageComplete())
  }

  private clearAllPlayData = () => {
    // Destroy all state (don't expect to retrieve it)
    if (this.timer) {
      this.timer.stop()
      delete this.timer
    }

    if (this.curHowl) {
      this.curHowl.stop()
      delete this.curHowl
    }

    if (this.progressInterval) {
      window.clearInterval(this.progressInterval)
      delete this.progressInterval
    }
  }

  private playerStateChangeHandler = (newState: PlayerState) => {
    if (!this.timer) {
      return
    }

    const { store } = this.injected

    if (newState === PlayerState.inactive) {
      if (this.curHowl) {
        this.curHowl.stop()
        delete this.curHowl
      }

      if (this.timer) {
        this.timer.stop()
        delete this.timer
      }

      // End interval to update timer
      window.clearInterval(this.progressInterval)
      delete this.progressInterval
    }

    // -------------------------------------------------------------------------
    // PlayerState.Ended
    // -------------------------------------------------------------------------
    if (newState === PlayerState.ended) {
      this.clearAllPlayData()

      if (store.visibleCards.length) {
        this.playCard()
      }

      return
    }

    // -------------------------------------------------------------------------
    // PlayerState.playing
    // -------------------------------------------------------------------------
    if (newState === PlayerState.playing) {
      if (this.timer && !this.timer.isPlaying) {
        this.timer.play()
      }

      if (this.curHowl && !this.curHowl.playing()) {
        this.curHowl.play()
      }

      // Start interval to update timer
      window.clearInterval(this.progressInterval)
      this.updateProgressPercentage()
      this.progressInterval = window.setInterval(
        this.updateProgressPercentage,
        1000
      )

      try {
        if (window && window.drift && typeof window.drift !== 'undefined') {
          window.drift.api.widget.hide()
          return
        }
      } catch (e) {
        console.error(e)
      }

      return
    }

    // -------------------------------------------------------------------------
    // PlayerState.paused
    // -------------------------------------------------------------------------
    if (newState === PlayerState.paused) {
      if (this.timer && this.timer.isPlaying) {
        this.timer.pause()
      }

      // Pause playback if it exists
      if (this.curHowl && this.curHowl.playing()) {
        this.curHowl.pause()
      }

      // End interval to update timer
      window.clearInterval(this.progressInterval)
      delete this.progressInterval

      return
    }

    // -------------------------------------------------------------------------
    // PlayerState.completed
    // -------------------------------------------------------------------------
    if (newState === PlayerState.completed) {
      try {
        if (window && window.drift && typeof window.drift !== 'undefined') {
          window.drift.api.widget.show()
          return
        }
      } catch (e) {
        console.error(e)
      }
    }
  }
}

export default Player



// WEBPACK FOOTER //
// ./src/components/Player.tsx